<template>
  <v-container fluid>
    <page-title>
      Veeva Vault Admin
      <template v-slot:buttons>
        <v-btn @click.prevent="fetchData">
          Refresh
        </v-btn>
      </template>
    </page-title>

    <v-row>
      <v-col cols="12"
             sm="4">
        <v-card>
          <v-card-title class="pb-0">
            Connection
          </v-card-title>
          <v-card-text>
            <v-select v-model="form.connection_id"
                      :items="connections"
                      item-value="id"
                      item-text="name">
            </v-select>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12"
             sm="4">
        <v-form @submit.prevent="describeObject">
          <v-card>
            <v-card-title class="pb-0">
              Describe Vault Object
            </v-card-title>
            <v-card-text>
              <v-autocomplete v-if="objects"
                              v-model.trim="form.object_name"
                              :items="objects"
                              item-value="id"
                              item-text="name__v"
                              clearable
                              @change="describeObject"
                              :error-messages="form.errors.get('object_name')">
              </v-autocomplete>
              <div v-if="object">
                <codemirror-json :value="JSON.stringify(object, null, 4)"
                                 :options="cmOptionsJSON">
                </codemirror-json>
              </div>
            </v-card-text>
          </v-card>
        </v-form>
      </v-col>

      <v-col cols="12"
             sm="4">
        <v-form @submit.prevent="findDocument">
          <v-card>
            <v-card-title class="pb-0">
              Retrieve Vault Document
            </v-card-title>
            <v-card-text>
              <v-text-field v-model.trim="form.document_number"
                            label="Document Number"
                            :error-messages="form.errors.get('document_number')">
              </v-text-field>
              <div v-if="document">
                <codemirror-json :value="JSON.stringify(document, null, 4)"
                                 :options="cmOptionsJSON">
                </codemirror-json>
              </div>
            </v-card-text>
          </v-card>
        </v-form>
      </v-col>

    </v-row>

    <v-row>

      <v-col cols="12"
             sm="4"
             v-if="therapeuticAreas && therapeuticAreas.length">
        <scroll-card title="Therapeutic Areas"
                     :form="queryForm"
                     field="therapeutic_areas"
                     :items="therapeuticAreas">
        </scroll-card>
      </v-col>

      <v-col cols="12"
             sm="4"
             v-if="products && products.length">
        <scroll-card title="Products"
                     :form="queryForm"
                     field="products"
                     :items="products">
        </scroll-card>
      </v-col>

      <v-col cols="12"
             sm="4"
             v-if="diseaseStates && diseaseStates.length">
        <scroll-card title="Disease States"
                     :form="queryForm"
                     field="disease_states"
                     :items="diseaseStates">
        </scroll-card>
      </v-col>

      <v-col cols="12"
             sm="4"
             v-if="countries && countries.length">
        <scroll-card title="Countries"
                     :form="queryForm"
                     field="countries"
                     :items="countries"
                     :card-height="200">
        </scroll-card>
      </v-col>

      <v-col cols="12"
             sm="4"
             v-if="objects && objects.length">
        <scroll-card title="Vault Objects"
                     :items="objects">
        </scroll-card>
      </v-col>

    </v-row>

    <v-row>
      <v-col cols="12">
        <v-form @submit.prevent="buildQuery">
          <v-card>
            <v-card-title>
              Build the VQL Document Query
            </v-card-title>
            <v-card-text>
              <p>
                You can build the query here and paste it into the <code>veeva_vault.php</code> configuration file.
              </p>
              <v-row>
                <v-col cols="4">
                  <v-text-field v-model="queryForm.type"
                                label="Type"
                                hide-details
                                dense>
                  </v-text-field>
                </v-col>
                <v-col cols="4">
                  <v-select v-model="queryForm.status"
                            label="Status"
                            :items="['Approved', 'Approved for Distribution', 'Approved for Use']"
                            hide-details
                            dense>
                  </v-select>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-btn @click.prevent="buildQuery">
                Build Query
              </v-btn>
              <v-btn @click.prevent="copyText(queryForm.vql)"
                     :disabled="!queryForm.vql">
                Copy to Clipboard
              </v-btn>
            </v-card-actions>
            <v-card-text v-if="queryForm.vql">
              <codemirror-query :code="queryForm.vql"
                                :options="cmOptionsQuery">
              </codemirror-query>
            </v-card-text>
          </v-card>
        </v-form>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-form @submit.prevent="executeQuery">
          <v-card>
            <v-card-title class="pb-0">
              Run a Vault Query
            </v-card-title>
            <v-card-text>
              <codemirror-query v-model="form.query"
                                :form="form"
                                :options="cmOptionsQuery"
                                class="mb-2">
              </codemirror-query>
              <div v-if="queryErrorMessage"
                   class="error--text mb-2">
                {{ queryErrorMessage }}
              </div>
              <div class="d-flex">
                <v-btn type="submit"
                       color="primary"
                       class="mr-2"
                       :disabled="form.busy || !form.query">
                  Run
                </v-btn>
                <v-btn @click.prevent="clearQuery"
                       class="mr-2">
                  Clear
                </v-btn>
                <v-btn href="https://developer.veevavault.com/vql/#introduction-to-vault-queries"
                       target="_blank">
                  Help
                </v-btn>
              </div>
              <div v-if="queryResults">
                <div>
                  {{ queryResults.data.length | pluralize('Result') }}
                </div>
                <codemirror-json :value="JSON.stringify(queryResults, null, 4)"
                                 :options="cmOptionsJSON">
                </codemirror-json>
              </div>
            </v-card-text>
          </v-card>
        </v-form>
      </v-col>
    </v-row>

  </v-container>
</template>
<script>
import {mapGetters} from 'vuex';
import {Form} from 'vform';
import {find, orderBy} from 'lodash'
import slugify from 'slugify';
import axios from '../../services/axiosService';
import veevaVaultService from '../../services/veevaVaultService';
import clipboardService from '../../../../ml/services/clipboardService';
import ScrollCard from './components/ScrollCard';
import CodemirrorQuery from './components/CodeMirrorQuery';
import CodemirrorJson from './components/CodeMirrorJson';
import PageTitle from '../../components/PageTitle.vue';

export default {
  components: {PageTitle, CodemirrorJson, CodemirrorQuery, ScrollCard},
  data() {
    return {
      cmOptionsQuery: { // MLD-2620
        //autoCloseTags: true,
        //extraKeys: {'Ctrl-Space': 'autocomplete'},
        //foldGutter: true,
        //gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter', 'CodeMirror-lint-markers'],
        lineNumbers: true,
        lineWrapping: true,
        //lint: true,
        //matchTags: {bothTags: true},
        mode: 'sql',
        theme: 'monokai',
      },
      cmOptionsJSON: { // MLD-2620
        //autoCloseTags: true,
        //extraKeys: {'Ctrl-Space': 'autocomplete'},
        //foldGutter: true,
        //gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter', 'CodeMirror-lint-markers'],
        lineNumbers: true,
        lineWrapping: true,
        //lint: true,
        //matchTags: {bothTags: true},
        mode: 'javascript',
        //readOnly: true,
        theme: 'monokai',
      },
      form: new Form({
        connection_id: '',
        object_name: '',
        document_number: '',
        query: 'SELECT id, document_number__v, name__v, title__v FROM documents',
      }),
      queryForm: new Form({
        countries: [
          'unitedStates',
        ],
        type: 'Field Medical',
        status: 'Approved',
        query_fields: [],
        therapeutic_areas: [],
        products: [],
        disease_states: [],
        constraints: [],
        vql: '',
      }),
      fields: [],
      connection: null,
      connections: [],
      countries: [],
      diseaseStates: [],
      document: null,
      object: null,
      objects: [],
      products: [],
      queryErrorMessage: null,
      queryResults: null,
      therapeuticAreas: [],
    }
  },
  computed: {
    ...mapGetters([
      'team',
      'user',
    ]),
    connectionID() {
      if (this.connection) {
        return this.connection.id;
      }
      return this.team.vault_connection_id;
    }
  },
  created() {
    this.form.connection_id = this.team.vault_connection_id;
    this.fetchData();
  },
  methods: {
    buildQuery() {
      this.queryForm.vql = '';
      const constraints = [];
      if (this.queryForm.countries.length) {
        let countries = this.queryForm.countries.map(s => `'${s}'`).join(', ');
        constraints.push(`AND country__v CONTAINS(${countries})`);
      }
      if (this.queryForm.therapeutic_areas.length) {
        let areas = this.queryForm.therapeutic_areas.map(s => `'${s}'`).join(', ');
        constraints.push(`AND therapeutic_area1__c CONTAINS(${areas})`);
      }
      if (this.queryForm.products.length) {
        let products = this.queryForm.products.map(s => `'${s}'`).join(', ');
        constraints.push(`AND product__v CONTAINS(${products})`);
      }
      if (this.queryForm.disease_states.length) {
        let diseaseStates = this.queryForm.disease_states.map(s => `'${s}'`).join(', ');
        constraints.push(`AND disease_state__c CONTAINS(${diseaseStates})`);
      }
      constraints.push(`{{ where_formats }}`)
      constraints.push(`{{ where_clause }}`)
      this.queryForm.constraints = constraints;
      this.queryForm.vql = `SELECT {{ query_fields }}
                            FROM documents
                            WHERE type__v = '${this.queryForm.type}'
                              AND status__v = '${this.queryForm.status}'
                              ${constraints.join('\n')}
                            ORDER BY {{ sort }} PAGESIZE {{ pagesize }} PAGEOFFSET {{ pageoffset }}`
        .replace(/\s{2,}/g, ' ')
    },
    copyText(text) {
      clipboardService.copy(text, false)
        .then(() => {
          this.$toast(`Copied ${text} to your clipboard`);
        })
    },
    async fetchData() {
      await veevaVaultService.getConnections()
        .then(data => {
          this.connections = data;
          this.connection = find(data, connection => connection.id === this.team.vault_connection_id);
          if (this.connection) {
            this.fields = this.connection.query_fields;
            this.queryForm.query_fields = this.connection.query_fields;
          }
        })

      /*axios.get(`/api/veeva/vault/metadata/vobjects`, {params: {connection_id: this.team.vault_connection_id}})
        .then(response => {
          this.objects = orderBy(response.data.objects.map(obj => {
            const slug = slugify(obj.label.replaceAll('-', '_'), {lower: true, replacement: '_', strict: true, trim: true});
            const ret = {
              ...obj,
              id: obj.name,
              slug: slug
            }
            ret.name__v = obj.label;
            return ret;
          }), ['slug'], ['asc']);
        }).catch(() => {});*/

      const params = {
        connection_id: this.connectionID,
        sort: 'name__v asc'
      }
      veevaVaultService.retrieveVaultObjects('country__v', params)
        .then(response => {
          this.countries = response.data;
        }).catch(() => {});

      veevaVaultService.retrieveVaultObjects('therapeutic_area__c', params)
        .then(response => {
          this.therapeuticAreas = response.data;
        }).catch(() => {});

      veevaVaultService.retrieveVaultObjects('product__v', params)
        .then(response => {
          this.products = response.data;
        }).catch(() => {});

      veevaVaultService.retrieveVaultObjects('disease_state__c', params)
        .then(response => {
          this.diseaseStates = response.data;
        }).catch(() => {});

    },
    findDocument() {
      if (this.form.busy) {
        return;
      }
      const form = {
        connection_id: this.team.vault_connection_id,
      }
      this.document = null;
      this.form.startProcessing();
      veevaVaultService.findDocumentByDocumentNumber(this.form.document_number, form)
        .then(data => {
          this.document = data;
          this.form.finishProcessing();
        })
        .catch(err => {
          this.form.errors.set('document_number', 'Document Number not found');
        })
        .finally(() => {
          this.form.busy = false;
        })
    },
    describeObject() {
      if (this.form.busy) {
        return;
      }
      this.object = null;
      this.form.startProcessing();
      const form = {
        connection_id: this.team.vault_connection_id,
      };
      return axios.get(`/api/veeva/vault/vobjects/${this.form.object_name}`, {query: form})
        .then(response => {
          this.object = response.data;
          this.form.finishProcessing();
        }).catch(() => {})
        .finally(() => {
          this.form.busy = false;
        })
    },
    decorate(result) {
      result.data = orderBy(result.data.map(obj => {
        const slug = slugify(obj.name__v.replaceAll('-', '_'), {lower: true, replacement: '_', strict: true, trim: true});
        return {
          ...obj,
          slug: slug,
        }
      }), ['slug'], ['asc']);
      return result;
    },
    executeQuery() {
      if (this.form.busy) {
        return;
      }
      const form = {
        connection: this.form.connection_id,
        q: this.form.query,
      }
      this.queryResults = null;
      this.queryErrorMessage = null;
      this.form.startProcessing();
      return axios.post(`/api/veeva/vault/raw-query`, form)
        .then(response => {
          this.queryResults = response.data;
          this.form.finishProcessing();
        })
        .catch(err => {
          this.queryErrorMessage = err.response.data.message;
        })
        .finally(() => {
          this.form.busy = false;
        })
    },
    clearQuery() {
      this.form.query = this.form.originalData.query;
      this.queryResults = null;
    },
  }
}
</script>
